import type { GameState, Pile } from '../../engine/game-state.js';
import { createGrid, CLASSIC_LAYOUT } from '../../render/classic/layout.js';
import type { ClassicSpec } from '../../render/classic/types.js';
import { CARD_W, CARD_H } from '../../render/classic/types.js';
import { acesupRules, dealRow, DISCARD_ID, TABLEAU_IDS } from './rules.js';

// 4-column grid. Top row: stock on the left, discard on the right.
// Bottom row: 4 fan-down tableau piles.
const COLS = 4;
const TOP_Y = 24;
const TABLEAU_Y = TOP_Y + CARD_H + CLASSIC_LAYOUT.ROW_GAP;
const FELT_W = COLS * CARD_W + (COLS - 1) * CLASSIC_LAYOUT.COL_GAP + 48;
// A pile never discarded from receives every deal: 1 + 12 = 13 cards fanned.
const FELT_H = TABLEAU_Y + CARD_H + 13 * CLASSIC_LAYOUT.FAN_DOWN + 24;

const layout = createGrid({
  cols: COLS,
  feltWidth: FELT_W,
  rows: [
    { y: TOP_Y, slots: ['stock', null, null, DISCARD_ID] },
    { y: TABLEAU_Y, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function dragStackIds(state: GameState, cardId: string): string[] {
  for (const pile of Object.values(state.piles)) {
    const idx = pile.cards.findIndex((c) => c.id === cardId);
    if (idx === -1) continue;
    if (pile.type !== 'tableau') return [];
    if (idx !== pile.cards.length - 1) return [];
    return [cardId];
  }
  return [];
}

function discard(state: GameState, pile: Pile, cardId: string): boolean {
  const move = { from: pile.id, to: DISCARD_ID, cardId };
  if (!acesupRules.canMove(state, move)) return false;
  const card = pile.cards.pop()!;
  state.piles[DISCARD_ID]!.cards.push(card);
  state.movesCount++;
  return true;
}

export const acesupClassicSpec: ClassicSpec = {
  rules: acesupRules,
  feltWidth: FELT_W,
  feltHeight: FELT_H,
  slotForPile: (id) => layout.slot(id),
  dropExclude: () => ['stock'],
  dragStackIds,
  autoSendOrder: () => [DISCARD_ID],
  onPileClick(state, pile, cardId) {
    if (pile.id === 'stock') {
      if (!dealRow(state)) return false;
      state.movesCount++;
      return true;
    }
    if (pile.type === 'tableau') {
      return discard(state, pile, cardId);
    }
    return false;
  },
  statusText(state) {
    const dealsLeft = Math.floor(state.piles.stock!.cards.length / TABLEAU_IDS.length);
    if (acesupRules.isLost!(state)) return 'No moves left';
    if (dealsLeft > 0) return `${dealsLeft} deal${dealsLeft === 1 ? '' : 's'} left`;
    return null;
  },
  cascadeCardIds(state) {
    return state.piles[DISCARD_ID]!.cards.map((c) => c.id);
  },
};
